/**
 * Configuration schemas and validators for different element types
 * Used with useElementConfig hook to provide type-specific validation and defaults
 */

import {
  STATISTIC_TYPE,
  CHART_DISPLAY_TYPE,
  LEGEND_POSITION,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  ConfigSchema,
  ConfigValidator,
  ConfigTransformer,
} from "features/Reporting/hooks/Elements/useElementConfig"
import {
  DataElementConfig,
  ChartElementSettings,
  PieChartElementSettings,
  TimeSeriesChartSettings,
  TableElementSettings,
  TextElementSettings,
  ImageElementSettings,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"

// Common statistic type options - derived from enum
export const STATISTIC_TYPES: string[] = Object.values(STATISTIC_TYPE)

// Common chart types - derived from enum
export const CHART_TYPES: string[] = Object.values(CHART_DISPLAY_TYPE)

// Common legend positions - derived from enum
export const LEGEND_POSITIONS: string[] = Object.values(LEGEND_POSITION)

/**
 * Base chart configuration schema
 */
export const BASE_CHART_SCHEMA: ConfigSchema = {
  selectedVariable: { required: true, type: "string" },
  selectedStatisticType: { required: false, type: "string", default: "n" },
  chartTitle: { required: false, type: "string", default: "" },
  chartDataUri: { required: false, type: "string" },
}

/**
 * Chart element configuration schema
 */
export const CHART_CONFIG_SCHEMA: ConfigSchema = {
  ...BASE_CHART_SCHEMA,
  breakdownVariable: { required: true, type: "string" },
  chartType: { required: false, type: "string", default: "bar" },
  showTotals: { required: false, type: "boolean", default: false },
  dashboardSelectedKeys: { required: false, type: "array", default: [] },
  dashboardSelectedCategories: { required: false, type: "array", default: [] },
  tableOperationType: {
    required: false,
    type: "string",
    default: TABLE_OPERATION_TYPE.XTAB,
  },
}

/**
 * Pie chart element configuration schema
 */
export const PIE_CHART_CONFIG_SCHEMA: ConfigSchema = {
  ...BASE_CHART_SCHEMA,
  legendPosition: { required: false, type: "string", default: "right" },
  hideArcLabels: { required: false, type: "boolean", default: false },
  dashboardSelectedCategories: { required: false, type: "array", default: [] },
}

/**
 * Time series chart element configuration schema
 */
export const TIME_SERIES_CONFIG_SCHEMA: ConfigSchema = {
  selectedPrimaryVariable: { required: true, type: "string" },
  selectedBreakdownVariable: { required: true, type: "string" },
  selectedWaveVariable: { required: true, type: "string" },
  selectedStatisticType: { required: false, type: "string", default: "n" },
  chartDataUri: { required: false, type: "string" },
  selectedDashboardSeries: { required: false, type: "array", default: [] },
  selectedDashboardWaves: { required: false, type: "array", default: [] },
  tableOperationType: {
    required: false,
    type: "string",
    default: TABLE_OPERATION_TYPE.XTAB,
  }, // ← ADD THIS LINE
}

/**
 * Table element configuration schema
 */
export const TABLE_CONFIG_SCHEMA: ConfigSchema = {
  crossTabVariables: {
    required: true,
    type: "array",
    minLength: 2,
    maxLength: 2,
  },
  selectedOperators: {
    required: false,
    type: "array",
    default: Object.values(STATISTIC_TYPE).filter(
      (type) => type !== STATISTIC_TYPE.AVG
    ),
  },
  isTransposed: { required: false, type: "boolean", default: false },
  originalWidth: { required: false, type: "number" },
  tableData: { required: false, type: "object" },
  tableOperationType: {
    required: false,
    type: "string",
    default: TABLE_OPERATION_TYPE.XTAB,
  },
}

/**
 * Text element configuration schema
 */
export const TEXT_CONFIG_SCHEMA: ConfigSchema = {
  text: { required: false, type: "string", default: "" },
  fontSize: { required: false, type: "number", default: 16 },
  color: { required: false, type: "string", default: "#000000" },
  isBold: { required: false, type: "boolean", default: false },
  isItalic: { required: false, type: "boolean", default: false },
  isUnderline: { required: false, type: "boolean", default: false },
  textAlign: { required: false, type: "string", default: "left" },
}

/**
 * Image element configuration schema
 */
export const IMAGE_CONFIG_SCHEMA: ConfigSchema = {
  imageSrc: { required: false, type: "string" },
  alt: { required: false, type: "string", default: "" },
}

/**
 * Default configurations for each element type
 */
export const DEFAULT_CONFIGS: Record<string, DataElementConfig> = {
  [ELEMENT_TYPE.CHART]: {
    selectedVariable: null,
    breakdownVariable: null,
    selectedStatisticType: STATISTIC_TYPE.N,
    chartTitle: "Chart",
    chartType: CHART_DISPLAY_TYPE.BAR,
    showTotals: false,
    dashboardSelectedKeys: [],
    dashboardSelectedCategories: [],
    chartDataUri: null,
    tableOperationType: TABLE_OPERATION_TYPE.XTAB,
  } as ChartElementSettings,

  [ELEMENT_TYPE.PIE_CHART]: {
    selectedVariable: null,
    selectedStatisticType: STATISTIC_TYPE.N,
    chartTitle: "Pie Chart",
    legendPosition: LEGEND_POSITION.RIGHT,
    hideArcLabels: false,
    dashboardSelectedCategories: [],
    chartDataUri: null,
  } as PieChartElementSettings,

  [ELEMENT_TYPE.TIME_SERIES_CHART]: {
    selectedPrimaryVariable: null,
    selectedBreakdownVariable: null,
    selectedWaveVariable: null,
    selectedStatisticType: STATISTIC_TYPE.N,
    selectedDashboardSeries: [],
    selectedDashboardWaves: [],
    chartDataUri: null,
    tableOperationType: TABLE_OPERATION_TYPE.XTAB,
  } as TimeSeriesChartSettings,

  [ELEMENT_TYPE.TABLE]: {
    crossTabVariables: [],
    selectedOperators: Object.values(STATISTIC_TYPE).filter(
      (type) => type !== STATISTIC_TYPE.AVG
    ),
    isTransposed: false,
    originalWidth: null,
    tableOperationType: TABLE_OPERATION_TYPE.XTAB,
  } as TableElementSettings,

  [ELEMENT_TYPE.TEXT]: {
    text: "",
    fontSize: 16,
    color: "#000000",
    isBold: false,
    isItalic: false,
    isUnderline: false,
    textAlign: "left",
  } as TextElementSettings,

  [ELEMENT_TYPE.HEADING]: {
    text: "",
    fontSize: 24,
    color: "#000000",
    isBold: true,
    isItalic: false,
    isUnderline: false,
    textAlign: "left",
  } as TextElementSettings,

  [ELEMENT_TYPE.IMAGE]: {
    imageSrc: null,
    alt: "",
  } as ImageElementSettings,
}

/**
 * Configuration schemas mapped by element type
 */
export const CONFIG_SCHEMAS: Record<string, ConfigSchema> = {
  [ELEMENT_TYPE.CHART]: CHART_CONFIG_SCHEMA,
  [ELEMENT_TYPE.PIE_CHART]: PIE_CHART_CONFIG_SCHEMA,
  [ELEMENT_TYPE.TIME_SERIES_CHART]: TIME_SERIES_CONFIG_SCHEMA,
  [ELEMENT_TYPE.TABLE]: TABLE_CONFIG_SCHEMA,
  [ELEMENT_TYPE.TEXT]: TEXT_CONFIG_SCHEMA,
  [ELEMENT_TYPE.HEADING]: TEXT_CONFIG_SCHEMA, // Same as text
  [ELEMENT_TYPE.IMAGE]: IMAGE_CONFIG_SCHEMA,
}

/**
 * Validators for specific element types
 */
export const CONFIG_VALIDATORS: Record<string, ConfigValidator> = {
  /**
   * Chart element config validator
   */
  [ELEMENT_TYPE.CHART]: (config: DataElementConfig) => {
    const chartConfig = config as ChartElementSettings
    const errors: string[] = []

    if (!chartConfig.selectedVariable || !chartConfig.breakdownVariable) {
      errors.push("Both primary and breakdown variables are required")
    }

    if (
      chartConfig.selectedStatisticType &&
      !STATISTIC_TYPES.includes(chartConfig.selectedStatisticType)
    ) {
      errors.push(
        `Invalid statistic type: ${chartConfig.selectedStatisticType}`
      )
    }

    if (chartConfig.chartType && !CHART_TYPES.includes(chartConfig.chartType)) {
      errors.push(`Invalid chart type: ${chartConfig.chartType}`)
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  },

  /**
   * Pie chart element config validator
   */
  [ELEMENT_TYPE.PIE_CHART]: (config: DataElementConfig) => {
    const pieConfig = config as PieChartElementSettings
    const errors: string[] = []

    if (!pieConfig.selectedVariable) {
      errors.push("Variable is required")
    }

    if (
      pieConfig.selectedStatisticType &&
      !STATISTIC_TYPES.includes(pieConfig.selectedStatisticType)
    ) {
      errors.push(`Invalid statistic type: ${pieConfig.selectedStatisticType}`)
    }

    if (
      pieConfig.legendPosition &&
      !LEGEND_POSITIONS.includes(pieConfig.legendPosition)
    ) {
      errors.push(`Invalid legend position: ${pieConfig.legendPosition}`)
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  },

  /**
   * Time series chart element config validator
   */
  [ELEMENT_TYPE.TIME_SERIES_CHART]: (config: DataElementConfig) => {
    const timeConfig = config as TimeSeriesChartSettings
    const errors: string[] = []

    if (
      !timeConfig.selectedPrimaryVariable ||
      !timeConfig.selectedBreakdownVariable ||
      !timeConfig.selectedWaveVariable
    ) {
      errors.push("All three variables (primary, breakdown, wave) are required")
    }

    if (
      timeConfig.selectedStatisticType &&
      !STATISTIC_TYPES.includes(timeConfig.selectedStatisticType)
    ) {
      errors.push(`Invalid statistic type: ${timeConfig.selectedStatisticType}`)
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  },

  /**
   * Table element config validator
   */
  [ELEMENT_TYPE.TABLE]: (config: DataElementConfig) => {
    const tableConfig = config as TableElementSettings
    const errors: string[] = []

    if (
      !Array.isArray(tableConfig.crossTabVariables) ||
      tableConfig.crossTabVariables.length !== 2
    ) {
      errors.push("Exactly two cross-tab variables are required")
    }

    if (
      tableConfig.selectedOperators &&
      !Array.isArray(tableConfig.selectedOperators)
    ) {
      errors.push("Selected operators must be an array")
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  },

  /**
   * Text/heading element config validator
   */
  [ELEMENT_TYPE.TEXT]: (config: DataElementConfig) => {
    const textConfig = config as TextElementSettings
    const errors: string[] = []

    if (
      textConfig.fontSize &&
      (typeof textConfig.fontSize !== "number" ||
        textConfig.fontSize < 8 ||
        textConfig.fontSize > 72)
    ) {
      errors.push("Font size must be between 8 and 72")
    }

    if (textConfig.color && !/^#[0-9A-F]{6}$/i.test(textConfig.color)) {
      errors.push("Color must be a valid hex code")
    }

    if (
      textConfig.textAlign &&
      !["left", "center", "right"].includes(textConfig.textAlign)
    ) {
      errors.push("Text align must be left, center, or right")
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  },
}

// Add heading validator (same as text)
CONFIG_VALIDATORS[ELEMENT_TYPE.HEADING] = CONFIG_VALIDATORS[ELEMENT_TYPE.TEXT]

/**
 * Config transformers for specific element types
 */
export const CONFIG_TRANSFORMERS: Record<string, ConfigTransformer> = {
  /**
   * Chart config transformer - ensures dashboard arrays are valid
   */
  [ELEMENT_TYPE.CHART]: (config: DataElementConfig) => {
    const chartConfig = config as ChartElementSettings
    return {
      ...chartConfig,
      dashboardSelectedKeys: Array.isArray(chartConfig.dashboardSelectedKeys)
        ? chartConfig.dashboardSelectedKeys
        : [],
      dashboardSelectedCategories: Array.isArray(
        chartConfig.dashboardSelectedCategories
      )
        ? chartConfig.dashboardSelectedCategories
        : [],
    }
  },

  /**
   * Text config transformer - ensures proper color format
   */
  [ELEMENT_TYPE.TEXT]: (config: DataElementConfig) => {
    const textConfig = config as TextElementSettings
    return {
      ...textConfig,
      color: textConfig.color?.startsWith("#")
        ? textConfig.color
        : `#${textConfig.color || "000000"}`,
    }
  },
}

// Add heading transformer (same as text)
CONFIG_TRANSFORMERS[ELEMENT_TYPE.HEADING] =
  CONFIG_TRANSFORMERS[ELEMENT_TYPE.TEXT]

/**
 * Element config setup interface
 */
export interface ElementConfigSetup {
  defaultConfig: DataElementConfig
  configSchema: ConfigSchema
  configValidator?: ConfigValidator
  configTransformer?: ConfigTransformer
}

/**
 * Utility function to get config setup for an element type
 *
 * @param elementType - The type of element to get config setup for
 * @returns Configuration setup object with defaults, schema, validator, and transformer
 *
 * @example
 * const setup = getElementConfigSetup(ELEMENT_TYPE.CHART)
 * // Returns: { defaultConfig, configSchema, configValidator, configTransformer }
 */
export const getElementConfigSetup = (
  elementType: string
): ElementConfigSetup => {
  return {
    defaultConfig: DEFAULT_CONFIGS[elementType] || ({} as DataElementConfig),
    configSchema: CONFIG_SCHEMAS[elementType] || {},
    configValidator: CONFIG_VALIDATORS[elementType],
    configTransformer: CONFIG_TRANSFORMERS[elementType],
  }
}
